/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import { RequiredValidation, CustomValidation, ValidationMessage } from 'sarsaparilla';
import { getClassName } from '../../utils/validationUtils';

const propTypes = {
    issuance: PropTypes.shape({
        id: PropTypes.string,
        is_commercially_guided: PropTypes.bool,
    }),
    handleIsGuidedChange: PropTypes.func,
    handleGuideFirstNameChange: PropTypes.func,
    handleGuideLastNameChange: PropTypes.func,
    handleGuideNumberChange: PropTypes.func,
    formValidationChange: PropTypes.func,
    validation: PropTypes.shape({}),
    displayValidationError: PropTypes.bool,
    isDisabled: PropTypes.bool,
};

const defaultProps = {
    isDisabled: false,
};

const GUIDE_DETAILS_PROP_PATH = 'guide_details';

export default class GuideDetails extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isGuided: props.issuance.is_commercially_guided,
            guideFirstName: get(props.issuance, 'guides[0].first_name', ''),
            guideLastName: get(props.issuance, 'guides[0].last_name', ''),
            guideNumber: get(props.issuance, 'guides[0].passholder_id', ''),
        };
        this.handleIsGuidedChange = this.handleIsGuidedChange.bind(this);
        this.handleGuideFirstNameChange = this.handleGuideFirstNameChange.bind(this);
        this.handleGuideLastNameChange = this.handleGuideLastNameChange.bind(this);
        this.handleGuideNumberChange = this.handleGuideNumberChange.bind(this);
    }

    onFormValidate(isInValid, error, path) {
        if (isInValid) {
            this.props.formValidationChange(`${this.props.issuance.id}_${path}`, error);
        } else {
            this.props.formValidationChange(`${this.props.issuance.id}_${path}`, null);
        }
    }

    handleIsGuidedChange(event) {
        const value = event.target.value;
        this.setState({
            isGuided: value === 'yes',
        });
        this.props.handleIsGuidedChange(value === 'yes');
    }

    handleGuideFirstNameChange(event) {
        const value = event.target.value;
        this.setState({
            guideFirstName: value,
        });
        this.props.handleGuideFirstNameChange(value);
    }

    handleGuideLastNameChange(event) {
        const value = event.target.value;
        this.setState({
            guideLastName: value,
        });
        this.props.handleGuideLastNameChange(value);
    }

    handleGuideNumberChange(event) {
        const value = event.target.value;
        this.setState({
            guideNumber: value,
        });
        this.props.handleGuideNumberChange(value);
    }

    render() {
        const { issuance, validation, isDisabled, displayValidationError } = this.props;
        if (!issuance) {
            return null;
        }
        const { id } = issuance;
        const isGuidedFailValidation = getClassName(
            get(validation, `${id}_${GUIDE_DETAILS_PROP_PATH}.is_commercially_guided`),
            displayValidationError
        );
        const firstNameFailValidation = getClassName(
            get(validation, `${id}_${GUIDE_DETAILS_PROP_PATH}.guides[0].first_name`),
            displayValidationError
        );
        const lastNameFailValidation = getClassName(
            get(validation, `${id}_${GUIDE_DETAILS_PROP_PATH}.guides[0].last_name`),
            displayValidationError
        );
        const guideNumberFailValidation = getClassName(
            get(validation, `${id}_${GUIDE_DETAILS_PROP_PATH}.guides[0].passholder_id`),
            displayValidationError
        );

        return (
            <div>
                <div
                    className={
                        isGuidedFailValidation ? 'permit-validation-highlight' : ''
                    }
                >
                    <h2 className="h5">Commercial Guide</h2>
                    <p>
                        Is this a commercially guided trip?
                        <mark title="required">*</mark>
                    </p>
                    <div
                        className="rec-form-inline-item-wrap"
                        role="group"
                        aria-label="Is this a commercially guided trip?"
                    >
                        <div className="rec-form-inline-item mb-0">
                            <label
                                htmlFor="commercial-guide-yes"
                                className={`rec-label-radio ${
                                    isDisabled ? 'disabled' : ''
                                }`}
                            >
                                <input
                                    id="commercial-guide-yes"
                                    type="radio"
                                    className="rec-input-hide"
                                    name="commercial-guide-prompt-yes"
                                    checked={this.state.isGuided === true}
                                    value="yes"
                                    disabled={isDisabled}
                                    onChange={this.handleIsGuidedChange}
                                />
                                <CustomValidation
                                    value={
                                        isNil(this.state.isGuided)
                                            ? ''
                                            : this.state.isGuided
                                    }
                                    displayInSummary
                                    customValidator={(value) => !isBoolean(value)}
                                    onValidated={(isInValid, error) =>
                                        this.onFormValidate(
                                            isInValid,
                                            error,
                                            `${GUIDE_DETAILS_PROP_PATH}.is_commercially_guided`
                                        )
                                    }
                                    controlLabel="Commercially Guided"
                                >
                                    <ValidationMessage
                                        controlName="Commercially Guided"
                                        message="is required."
                                    />
                                </CustomValidation>
                                <span
                                    className={`rec-input-radio ${
                                        isDisabled ? 'disabled' : ''
                                    }`}
                                />
                                Yes
                            </label>
                        </div>
                        <div className="rec-form-inline-item mb-0">
                            <label
                                htmlFor="commercial-guide-no"
                                className={`rec-label-radio ${
                                    isDisabled ? 'disabled' : ''
                                }`}
                            >
                                <input
                                    id="commercial-guide-no"
                                    type="radio"
                                    className="rec-input-hide"
                                    name="commercial-guide-prompt-no"
                                    checked={this.state.isGuided === false}
                                    value="no"
                                    disabled={isDisabled}
                                    onChange={this.handleIsGuidedChange}
                                />
                                <span
                                    className={`rec-input-radio ${
                                        isDisabled ? 'disabled' : ''
                                    }`}
                                />
                                No
                            </label>
                        </div>
                    </div>
                </div>
                {this.state.isGuided && (
                    <div className="rec-section-outer-wrap">
                        <div className="rec-nested-wrap">
                            <div className="rec-grid-4-12">
                                <div className="rec-form-item-wrap">
                                    <label htmlFor="guide-first-name">
                                        First Name
                                        <mark title="required">*</mark>
                                    </label>
                                    <input
                                        type="text"
                                        className={firstNameFailValidation}
                                        id="guide-first-name"
                                        value={this.state.guideFirstName}
                                        disabled={isDisabled}
                                        onChange={this.handleGuideFirstNameChange}
                                    />
                                    <RequiredValidation
                                        value={get(this.state, 'guideFirstName', '')}
                                        displayInSummary
                                        onValidated={(isInValid, error) =>
                                            this.onFormValidate(
                                                isInValid,
                                                error,
                                                `${GUIDE_DETAILS_PROP_PATH}.guides[0].first_name`
                                            )
                                        }
                                        controlLabel="Guide First Name"
                                    />
                                </div>
                            </div>
                            <div className="rec-grid-4-12">
                                <div className="rec-form-item-wrap">
                                    <label htmlFor="guide-last-name">
                                        Last Name
                                        <mark title="required">*</mark>
                                    </label>
                                    <input
                                        type="text"
                                        className={lastNameFailValidation}
                                        id="guide-last-name"
                                        value={this.state.guideLastName}
                                        disabled={isDisabled}
                                        onChange={this.handleGuideLastNameChange}
                                    />
                                    <RequiredValidation
                                        value={get(this.state, 'guideLastName', '')}
                                        displayInSummary
                                        onValidated={(isInValid, error) =>
                                            this.onFormValidate(
                                                isInValid,
                                                error,
                                                `${GUIDE_DETAILS_PROP_PATH}.guides[0].last_name`
                                            )
                                        }
                                        controlLabel="Guide Last Name"
                                    />
                                </div>
                            </div>
                            <div className="rec-grid-4-12">
                                <div className="rec-form-item-wrap">
                                    <label htmlFor="guide-number">
                                        Guide #<mark title="required">*</mark>
                                    </label>
                                    <input
                                        type="text"
                                        className={guideNumberFailValidation}
                                        id="guide-number"
                                        value={this.state.guideNumber}
                                        disabled={isDisabled}
                                        onChange={this.handleGuideNumberChange}
                                    />
                                    <RequiredValidation
                                        value={get(this.state, 'guideNumber', '')}
                                        displayInSummary
                                        onValidated={(isInValid, error) =>
                                            this.onFormValidate(
                                                isInValid,
                                                error,
                                                `${GUIDE_DETAILS_PROP_PATH}.guides[0].passholder_id`
                                            )
                                        }
                                        controlLabel="Guide Number"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <hr />
            </div>
        );
    }
}

GuideDetails.propTypes = propTypes;
GuideDetails.defaultProps = defaultProps;
